/**
 * Per-camera zoom-dependent visibility (Phase V.1 Part 1).
 *
 * Each camera marker + coverage cone is hidden until the Cesium camera is
 * close enough that the cone's footprint reads as a meaningful shape on screen
 * — not a sub-pixel speck. We express that with Cesium's
 * distanceDisplayCondition: an entity is drawn only while the eye-to-entity
 * distance is below `far`. At a top-down view that distance is ~= altitude, so
 * `far` is effectively "show below this altitude".
 *
 * `far` scales with the cone's real-world range so a 500 m highway cam blooms
 * in at city zoom (~30 km) while a 5 km wildfire-tower cone is visible from
 * much higher (~300 km). This replaces the old blanket "hide all cones when
 * >N cameras" hack, which suppressed cones uniformly regardless of zoom.
 */

import type { CameraFeature } from '@tsr/shared-types';

/** Range assumed for cameras whose range_m is missing / non-positive (the
 *  heuristic-default case). Keeps the math well-defined at the edges. */
export const DEFAULT_CONE_RANGE_M = 500;

/**
 * Altitude-to-range multiplier. A cone of range R becomes visible once the
 * camera descends below R * VISIBLE_RATIO metres. Tuned so a 500 m cone
 * appears at ~30 km altitude (city/region zoom) and a 5 km cone at ~300 km.
 */
export const VISIBLE_RATIO = 60;

/**
 * Compute the `far` distance (metres) for an entity's distanceDisplayCondition
 * from a camera's coverage range. Non-finite / zero / negative ranges fall back
 * to DEFAULT_CONE_RANGE_M so range_m=0 or NULL can't blank the entity out.
 */
export function coneFarDistance(rangeM: number | null | undefined): number {
  const r =
    typeof rangeM === 'number' && Number.isFinite(rangeM) && rangeM > 0
      ? rangeM
      : DEFAULT_CONE_RANGE_M;
  return r * VISIBLE_RATIO;
}

/* ------------------------------------------------------------------------- *
 *  Stage 5 §5.5 — country-zoom coverage tiering.
 *
 *  V.1 hid every camera (live + snapshot alike) until the eye descended below
 *  `coneFarDistance(range)` — so at the country/regional view the map showed no
 *  cameras at all. Stage 5 turns the map into a live-feed *coverage view*: an
 *  operator at strategic zoom should see the full live footprint, while dense
 *  urban snapshot clutter still tiers out so the country view stays readable.
 *
 *  The tier hierarchy (DO NOT collapse these into one rule — each row is a
 *  deliberate altitude band):
 *
 *    1. Live cams (any non-      → visible at ALL zoom (country included). This
 *       'snapshot' stream_type)     is the coverage view: hls/mjpeg/youtube_live/
 *                                  mp4_clip are the operationally-significant
 *                                  live feeds (incl. smoke-priority live cams).
 *    2. Smoke-priority snapshot  → bloom in at REGIONAL zoom (L.2.1 spec D). The
 *                                  wildfire-watch snapshot subset used to show at
 *                                  country zoom; demoted so the national Default
 *                                  view reads as live-feed coverage, not a wall
 *                                  of stale watch-frames. Live feeds stay country.
 *    3. Snapshot, rural/remote   → bloom in at REGIONAL zoom. Sparse cams carry
 *       (outside metro bboxes)      coverage signal, so we surface them earlier
 *                                  than dense urban ones, but not at country
 *                                  zoom (they'd pepper empty wilderness).
 *    4. Snapshot, urban-dense    → keep the V.1 per-range tiering: they only
 *       (inside a metro bbox)       appear once the eye is at city/street zoom,
 *                                  so a metro's hundreds of traffic cams don't
 *                                  swamp the country/regional view.
 *
 *  `far` is the max eye-to-entity distance (≈ camera altitude in a top-down
 *  view) at which the entity is drawn. Bigger `far` ⇒ visible from higher up.
 *
 *  NOTE on the urban/rural split: the spec's full rule is "outside major-metro
 *  bboxes OR <5 cams within 50 km". True neighbour-density needs a server-side
 *  spatial query (the viewport set is truncated and can't be counted client-
 *  side), so we implement the deterministic metro-bbox half here, which covers
 *  the dominant "dense city cluster vs sparse hinterland" case. A density-based
 *  refinement is a future server-side enhancement.
 * ------------------------------------------------------------------------- */

/** Eye distance (m) above which nothing in Canada is ever viewed top-down — so
 *  an entity with this `far` is effectively always visible, country zoom and
 *  in. A whole-Canada top-down sits at ~5,000 km altitude; 100,000 km clears it
 *  by a wide margin while staying finite (Cesium rejects an infinite far). */
export const COUNTRY_VISIBLE_FAR_M = 1.0e8;

/** Rural/remote snapshot cams bloom in once the eye drops below ~2,000 km —
 *  i.e. at provincial/regional zoom, but still hidden at the country view. */
export const REGIONAL_VISIBLE_FAR_M = 2.0e6;

/**
 * Major Canadian metro bounding boxes [west, south, east, north] (lon/lat).
 * Snapshot cams inside any of these are treated as "urban dense" and keep the
 * V.1 city/street tiering; everything else is "rural/remote". Generous padding
 * around each core so suburban traffic cams are captured too.
 */
export const METRO_BBOXES: ReadonlyArray<readonly [number, number, number, number]> = [
  [-79.9, 43.3, -78.9, 44.1], // Greater Toronto / GTA
  [-74.0, 45.2, -73.2, 45.8], // Montréal
  [-123.4, 49.0, -122.4, 49.5], // Metro Vancouver
  [-114.4, 50.8, -113.8, 51.2], // Calgary
  [-113.8, 53.3, -113.2, 53.8], // Edmonton
  [-76.0, 45.2, -75.4, 45.6], // Ottawa–Gatineau
  [-71.4, 46.7, -71.1, 46.95], // Québec City
  [-97.4, 49.7, -96.9, 50.0], // Winnipeg
  [-63.8, 44.5, -63.4, 44.8], // Halifax
  [-104.7, 50.3, -104.4, 50.55], // Regina
  [-106.8, 52.0, -106.5, 52.25], // Saskatoon
];

/* ------------------------------------------------------------------------- *
 *  Task #48 — coverage-footprint opacity.
 *
 *  A camera's cone/disc fill alpha layers three rules on top of the V.2
 *  altitude ramp (MapView `coneAltitudeAlpha`, ~0.15 near → 0.5 far):
 *
 *    1. Shape register (brightest = most directional information):
 *         directional cone  >  heading-unknown disc  >  PTZ 360° disc
 *       A cone aims somewhere specific; a heading-unknown disc is ambient
 *       "we cover roughly here" context; a PTZ disc is a rotating cam whose
 *       big circle must never dominate the map.
 *    2. Health (Task #48a): a broken / unavailable camera multiplies its fill
 *       down by FOOTPRINT_UNHEALTHY_DIM so dead coverage never paints bright —
 *       in lockstep with the already-dimmed marker.
 *    3. Hover: +20% "clickable" affordance bump, applied last and clamped.
 * ------------------------------------------------------------------------- */

/** Fixed opacity for PTZ 360° coverage discs (Phase Q.2.5.3). Held low so a
 *  large rotating-cam coverage circle never dominates the map; unlike cones it
 *  doesn't use the V.2 altitude ramp (which would over-paint a big disc
 *  close-in). This is the floor of the three shape registers. */
export const PTZ_DISC_ALPHA = 0.15;

/** Heading-unknown (non-PTZ) coverage discs are lower-information ambient
 *  context than a directional cone, so they render dimmer (Task #48b): a
 *  fraction of the cone's altitude ramp — always below the cone at a given
 *  altitude — floored above PTZ_DISC_ALPHA so the two disc kinds stay distinct.
 *  At far zoom: cone 0.5 → disc 0.275 → PTZ 0.15. */
export const UNKNOWN_DISC_ALPHA_SCALE = 0.55;
export const UNKNOWN_DISC_ALPHA_MIN = 0.2;

/** Broken / unavailable cameras dim their footprint in lockstep with the
 *  dimmed marker (Task #48a). Same 0.4 factor the marker uses (MapView
 *  `UNHEALTHY_ALPHA`); kept here so footprint + marker dim together. */
export const FOOTPRINT_UNHEALTHY_DIM = 0.4;

export type FootprintShape = 'cone' | 'unknownDisc' | 'ptzDisc';

export interface FootprintAlphaInput {
  shape: FootprintShape;
  /** The altitude-driven cone opacity for this frame (MapView coneAltitudeAlpha). */
  coneRampAlpha: number;
  /** url_health is probed and not HEALTHY → dim in lockstep with the marker. */
  unhealthy: boolean;
  /** Cursor is over this footprint → +20% affordance bump. */
  hovered: boolean;
}

/**
 * Final per-frame fill opacity for a camera's coverage footprint. Pure so the
 * shape/health/hover layering is unit-tested independently of Cesium.
 */
export function footprintAlpha(input: FootprintAlphaInput): number {
  const { shape, coneRampAlpha, unhealthy, hovered } = input;
  let a: number;
  switch (shape) {
    case 'ptzDisc':
      a = PTZ_DISC_ALPHA;
      break;
    case 'unknownDisc':
      a = Math.max(UNKNOWN_DISC_ALPHA_MIN, coneRampAlpha * UNKNOWN_DISC_ALPHA_SCALE);
      break;
    case 'cone':
    default:
      a = coneRampAlpha;
      break;
  }
  if (unhealthy) a *= FOOTPRINT_UNHEALTHY_DIM;
  if (hovered) a *= 1.2;
  return Math.min(1, a);
}

/** True when (lat, lon) falls inside any major-metro bbox (= "urban dense"). */
export function isUrbanDense(lat: number, lon: number): boolean {
  return METRO_BBOXES.some(
    ([w, s, e, n]) => lon >= w && lon <= e && lat >= s && lat <= n,
  );
}

export interface CameraVisibilityInput {
  streamType: string;
  smokePriority: boolean;
  lat: number;
  lon: number;
  rangeM: number | null | undefined;
}

/**
 * Stage 5 §5.5 — the `far` distance for a camera's distanceDisplayCondition,
 * applying the four-tier coverage hierarchy above. Replaces the bare
 * `coneFarDistance(range_m)` call MapView used in V.1.
 */
export function cameraVisibilityFar(cam: CameraVisibilityInput): number {
  // Tier 1: any live (non-snapshot) feed always visible — the coverage view.
  // This includes smoke-priority live cams (they're never snapshots).
  if (cam.streamType !== 'snapshot') return COUNTRY_VISIBLE_FAR_M;
  // Tier 2: smoke-priority snapshot blooms in at regional zoom (L.2.1 spec D —
  // demoted from country so the national Default view stays a live-feed picture).
  if (cam.smokePriority) return REGIONAL_VISIBLE_FAR_M;
  // Tier 3: rural/remote snapshot blooms in at regional zoom.
  if (!isUrbanDense(cam.lat, cam.lon)) return REGIONAL_VISIBLE_FAR_M;
  // Tier 4: urban-dense snapshot keeps the V.1 per-range city/street tiering.
  return coneFarDistance(cam.rangeM);
}

/** `cameraVisibilityFar` over a fetched CameraFeature (the adapter every
 *  feature-shaped caller needs — marker render, tier filter, cluster gate). */
export function cameraFeatureVisibilityFar(f: CameraFeature): number {
  const [lon, lat] = f.geometry.coordinates;
  return cameraVisibilityFar({
    streamType: f.properties.stream_type,
    smokePriority: f.properties.smoke_priority,
    lat,
    lon,
    rangeM: f.properties.range_m,
  });
}

/**
 * #255 founder ruling (i) — the cameras individually visible at the current
 * eye height, i.e. those whose own tier `far` admits this zoom. Cluster
 * membership MUST be computed over this set, never the whole fetched fleet: a
 * cluster may only aggregate cameras the operator could see individually at
 * this zoom (at country zoom that is the live-video tier ONLY; snapshot tiers
 * join as the eye descends through regional/city).
 */
export function tierVisibleCameras(
  features: readonly CameraFeature[],
  eyeHeightM: number,
): CameraFeature[] {
  return features.filter((f) => cameraFeatureVisibilityFar(f) >= eyeHeightM);
}

/**
 * #255 — a cluster marker's own zoom gate: the MOST RESTRICTIVE member's tier
 * `far`. Membership is recomputed per re-cluster (moveEnd), so between events
 * a cluster's composition is stale the moment the eye rises past the tightest
 * member's tier — gating the marker there hides it until the re-cluster
 * rebuilds honest groupings (a snapshot-inclusive cluster can never linger at
 * country zoom mid-zoom-out).
 */
export function clusterVisibilityFarM(members: readonly CameraFeature[]): number {
  let min = COUNTRY_VISIBLE_FAR_M;
  for (const m of members) min = Math.min(min, cameraFeatureVisibilityFar(m));
  return min;
}
